import { ReactElement, useEffect, useState } from 'react';
import { Address } from '@stripe/stripe-js';
import { useDispatch } from 'react-redux';

import {
  defaultBillingPeriod,
  selectAmounts,
  selectNumberOfLicenses,
  selectTrackGroup,
  setAmounts,
  setNumberOfLicenses,
  setTrackGroup,
} from '../../../features/signUp/signUpSlice';
import {
  useApplyCouponMutation,
  useGetAllPricesQuery,
  useLazyCalculateTaxQuery,
} from '../../../services/apiSlice';
import { useAppSelector } from '../../../stores/AppStore';

import { Skeleton } from '../../ui/skeleton';
import { handleError } from '../../../services/ErrorService';
import { getPrice, getPricePerLicense } from '../../../lib/utils';
import toast from 'react-hot-toast';
import BillingPeriodSelector from '../../BillingPeriodSelector';
import { BillingPeriod, TrackGroup } from '../../../types';
import { AcademicCapIcon, BookOpenIcon } from '@heroicons/react/outline';
import { TRIAL_PERIOD_DAYS } from '../../../constants';

interface OrderDetailsProperties {
  // paymentIntentId: string;
  address?: Address;
  isApplyCouponBlocked: boolean;
  setIsLoading: (value: boolean) => void;
}

const OrderDetails = ({
  address,
  isApplyCouponBlocked,
  setIsLoading,
}: OrderDetailsProperties) => {
  const dispatch = useDispatch();
  const [showContactUs, setShowContactUs] = useState(false);
  const numberOfLicenses = useAppSelector(selectNumberOfLicenses);
  const trackGroup = useAppSelector(selectTrackGroup);
  const amounts = useAppSelector(selectAmounts);
  const [couponCode, setCouponCode] = useState('');
  const { data: allPrices } = useGetAllPricesQuery();
  const [applyCoupon, applyCouponResponse] = useApplyCouponMutation();
  const [calculateTaxTrigger, { isFetching }] = useLazyCalculateTaxQuery();

  const selectedBillingPeriod: BillingPeriod =
    (amounts?.billingPeriod as BillingPeriod) || defaultBillingPeriod;

  // Comprehensive effect to handle all price calculations atomically
  // This prevents race conditions between price, discount, and tax calculations
  useEffect(() => {
    const calculateEverything = async () => {
      // Validate prerequisites
      if (!allPrices || allPrices.length === 0) {
        return;
      }

      if (numberOfLicenses === 0) {
        return;
      }

      // Handle 4+ licenses
      if (numberOfLicenses > 3) {
        dispatch(
          setAmounts({
            amount: 0,
            discount: undefined,
            tax: undefined,
            total: undefined,
          }),
        );
        return;
      }

      try {
        // Calculate base price
        const priceInDollars = getPrice(
          numberOfLicenses,
          amounts.billingPeriod as BillingPeriod,
          allPrices,
        );

        if (priceInDollars <= 0) {
          console.error('Invalid price returned:', priceInDollars);
          return;
        }

        const baseAmount = Math.round(100 * priceInDollars);

        // Calculate discount if applicable
        let discountAmount = 0;

        if (baseAmount > 0 && amounts.discountCode) {
          try {
            setIsLoading(true);
            const updateResponse = await applyCoupon({
              amount: baseAmount,
              couponCode: amounts.discountCode,
            });
            setIsLoading(false);

            if ('error' in updateResponse) {
              console.warn(
                'Discount application failed:',
                updateResponse.error,
              );
              toast.error('Applying coupon code failed. Please try again.');
            } else {
              discountAmount = updateResponse.data.discount_amount ?? 0;
            }
          } catch (error) {
            console.error('Discount calculation error:', error);
            toast.error('Applying coupon code failed. Please try again.');
            setIsLoading(false);
          }
        }

        // Update base amount and discount (tax will be calculated separately)
        const newAmounts = {
          amount: baseAmount,
          discount: discountAmount > 0 ? discountAmount : undefined,
          // Clear tax and total - they will be recalculated by the tax useEffect
          tax: undefined,
          total: undefined,
        };

        dispatch(setAmounts(newAmounts));
      } catch (error) {
        console.error('Price calculation error:', error);
        setIsLoading(false);
      }
    };

    calculateEverything();
  }, [
    allPrices,
    numberOfLicenses,
    amounts.billingPeriod,
    amounts.discountCode,
    dispatch,
    applyCoupon,
    setIsLoading,
  ]);

  // Separate effect to handle tax recalculation when only address changes
  // This prevents unnecessary price recalculation when user just enters address
  useEffect(() => {
    const recalculateTax = async () => {
      // Only recalculate tax if we have a base amount already calculated
      if (!amounts.amount || amounts.amount <= 0 || !address) {
        return;
      }

      // Use the existing amount (after any discounts) for tax calculation
      const taxableAmount = amounts.discount
        ? amounts.amount - amounts.discount
        : amounts.amount;

      if (taxableAmount <= 0) {
        return;
      }

      try {
        setIsLoading(true);
        const result = await calculateTaxTrigger({
          address,
          amount: taxableAmount,
        });
        setIsLoading(false);

        if (result.data && !result.error) {
          const taxAmount = result.data.tax_amount_exclusive;
          const totalAmount = Math.round(taxableAmount + taxAmount);

          // Only update tax and total, preserve existing amount and discount
          dispatch(
            setAmounts({
              tax: taxAmount > 0 ? taxAmount : undefined,
              total: totalAmount > 0 ? totalAmount : undefined,
            }),
          );
        } else {
          console.error('Tax calculation failed:', result.error);
        }
      } catch (error) {
        console.error('Tax calculation error:', error);
        setIsLoading(false);
      }
    };

    recalculateTax();
  }, [
    address,
    amounts.amount,
    amounts.discount,
    dispatch,
    calculateTaxTrigger,
    setIsLoading,
  ]);

  const handleApplyCoupon = () => {
    if (couponCode.length === 0) {
      toast.error('Please enter a valid coupon code');
      return;
    }
    dispatch(
      setAmounts({
        discountCode: couponCode.trim().toUpperCase(),
      }),
    );
  };

  const handleTrackGroupChange = (group: TrackGroup) => {
    try {
      dispatch(setTrackGroup(group));
      const newBillingPeriod: BillingPeriod =
        group === TrackGroup.ACTSATPrep
          ? 'year'
          : (amounts.billingPeriod as BillingPeriod) ?? 'month';
      if (newBillingPeriod !== amounts.billingPeriod) {
        setBillingPeriod(newBillingPeriod);
      }
    } catch (error) {
      handleError(error);
    }
  };

  const setBillingPeriod = (billingPeriod: BillingPeriod) => {
    dispatch(
      setAmounts({
        billingPeriod,
      }),
    );
  };

  const TrackGroupCard = ({
    group,
    icon,
  }: {
    group: TrackGroup;
    icon: ReactElement;
  }) => {
    return (
      <div
        className={`
          relative flex flex-col items-center w-full rounded-xl border border-gray-300 p-4 aspect-square cursor-pointer
          hover:shadow-md hover:outline hover:outline-tttDefault hover:outline-[1px] hover:bg-tttDefault hover:bg-opacity-5
          ${
            trackGroup === group
              ? 'shadow-md outline outline-tttDefault outline-[1px] bg-tttDefault bg-opacity-5'
              : ''
          }
        `}
        onClick={() => handleTrackGroupChange(group)}
      >
        <div className="h-[3rem] flex items-center flex-col justify-center">
          <p className="text-gray-600 text-sm lg:text-base font-semibold line-clamp-2 text-center">
            {group}
          </p>
          {group === TrackGroup.gradeLevelMath && (
            <p className="text-gray-500 text-sm mt-1 text-center">5th - 12th</p>
          )}
        </div>
        <div className="w-16 h-16 text-gray-500 mt-2">{icon}</div>
      </div>
    );
  };

  const getDisplayPrice = (numberOfLicenses: number) => {
    // For 4+ licenses, we'll show a contact message instead
    if (numberOfLicenses >= 4) {
      return '';
    }

    if (!allPrices || allPrices.length === 0) {
      return '?';
    }

    try {
      const price = getPricePerLicense(
        numberOfLicenses,
        selectedBillingPeriod,
        allPrices ?? [],
      );
      const displayPrice = price > 0 ? price.toFixed(2) : '?';
      return displayPrice;
    } catch (error) {
      console.warn(
        'Error getting display price for',
        numberOfLicenses,
        'licenses:',
        error,
      );
      return '?';
    }
  };

  return (
    <div>
      <h1 className="text-2xl md:text-3xl font-medium w-full pb-2 md:pb-4">
        Step 1: Enroll Now
      </h1>
      <div
        data-testid="full-order-details"
        className="flex flex-col gap-4 md:w-[90%] md:bg-gray-100 text-gray-800 rounded-xl md:p-8 animate-slideInFromRight animate-slideOutToLeft"
      >
        <div className="text-normal text-gray-500">
          Choose your learning track below.
        </div>
        <div className="flex w-full lg:w-3/4 gap-4 justify-around mx-auto">
          <TrackGroupCard
            group={TrackGroup.ACTSATPrep}
            icon={<AcademicCapIcon />}
          />
          <TrackGroupCard
            group={TrackGroup.gradeLevelMath}
            icon={<BookOpenIcon />}
          />
        </div>
        <div data-testid="number-of-licenses" className="flex gap-2">
          <div className="mx-auto">
            {/* 7-Day Free Trial Banner */}
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-center">
              <div className="flex items-center justify-center gap-2 mb-1">
                <span className="text-green-600 font-semibold text-lg">
                  🎉 {TRIAL_PERIOD_DAYS}-Day Free Trial
                </span>
              </div>
              <p className="text-green-700 text-sm">
                Start your subscription with a {TRIAL_PERIOD_DAYS}-day free
                trial. No payment required to begin!
              </p>
              <p className="text-green-600 text-xs mt-1">
                Cancel anytime during the trial period at no cost.
              </p>
            </div>

            <BillingPeriodSelector
              selected={selectedBillingPeriod}
              onChange={setBillingPeriod}
              disabledMonthly={trackGroup === TrackGroup.ACTSATPrep}
              disabledWarning={
                'ACT/SAT Math Prep courses are only available for an annual purchase option'
              }
            />
            <p className="text-center text-gray-500">
              {selectedBillingPeriod === 'year'
                ? '23 weeks free! 🎉'
                : selectedBillingPeriod === '3month'
                ? 'Great value - pay quarterly! 💰'
                : 'Check our offer on longer plans!'}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap justify-center gap-2 max-w-full">
          {Array.from({ length: 4 }, (_, index) => (
            <div key={index} className="flex-none w-[90px] md:w-24">
              <div
                className={`
                      relative flex flex-col w-full rounded-xl border border-gray-300 p-2 aspect-square cursor-pointer
                      hover:shadow-md hover:outline hover:outline-tttDefault hover:outline-[1px] hover:bg-tttDefault hover:bg-opacity-5
                      ${
                        numberOfLicenses === index + 1
                          ? 'shadow-md outline outline-tttDefault outline-[1px] bg-tttDefault bg-opacity-5'
                          : ''
                      }
                    `}
                onClick={() => {
                  dispatch(setNumberOfLicenses(index + 1));
                  setShowContactUs(index === 3);
                }}
              >
                <input
                  type="hidden"
                  id={`license-${index + 1}`}
                  data-testid={`license-${index + 1}`}
                  value={index + 1}
                  className="w-5 h-5 place-self-start absolute cursor-pointer"
                />
                <label
                  htmlFor={`license-${index + 1}`}
                  className="flex flex-col gap-1 text-center text-l md:text-xl w-full mx-auto mt-0 mb-auto cursor-pointer"
                >
                  {index === 3 ? '4+' : index + 1}{' '}
                  <span className="text-gray-500 text-xs md:text-sm mt-[-4px] md:mt-[-8px]">
                    Student{index > 0 ? 's' : ''}
                  </span>
                  <div className="w-full px-0.5">
                    {index === 3 ? (
                      <div className="flex flex-col gap-1">
                        <p className="rounded-full bg-blue-100 text-tttDefault text-xs py-0.5 px-1 cursor-pointer leading-tight font-medium">
                          Contact Us
                        </p>
                      </div>
                    ) : (
                      <p className="rounded-full bg-blue-100 text-tttDefault text-xs md:text-sm py-0.5 px-0.5 md:px-1 cursor-pointer leading-tight">
                        <strong className="whitespace-nowrap">
                          ${getDisplayPrice(index + 1)}
                        </strong>
                        {index > 0 && (
                          <span className="block text-xs leading-tight">
                            each
                          </span>
                        )}
                      </p>
                    )}
                  </div>
                </label>
              </div>
            </div>
          ))}
        </div>
        {showContactUs && (
          <p className="text-normal text-gray-500 py-2">
            Contact us at{' '}
            <a
              href="mailto:help@thinktankprep.com?subject=More Licenses"
              className="text-tttDefault hover:underline"
            >
              help@thinktankprep.com
            </a>{' '}
            to set up your subscriptions.
          </p>
        )}
        {!showContactUs && (
          <>
            <p className="text-xs text-gray-500">
              Track selection takes place after purchase
            </p>
            <div className="flex flex-col gap-2 mt-auto">
              <label htmlFor="couponCode" className="font-medium">
                Discount Code
              </label>
              <div className="flex flex-row gap-2">
                <input
                  type="text"
                  id="couponCode"
                  className="border border-gray-300 rounded-md px-3 py-1.5 w-full"
                  placeholder="Enter your discount code"
                  value={couponCode}
                  onChange={(event) => {
                    // Only allow alphanumeric characters and convert to uppercase
                    const filteredValue = event.target.value
                      .replace(/[^\dA-Za-z]/g, '')
                      .toUpperCase();
                    setCouponCode(filteredValue);
                  }}
                />
                <button
                  data-testid={`apply-coupon-button`}
                  onClick={handleApplyCoupon}
                  disabled={
                    isApplyCouponBlocked ||
                    isFetching ||
                    applyCouponResponse.isLoading
                  }
                  className="
            px-6 py-1.5 rounded-full border border-tttDefault text-tttDefault font-medium
            transition-all duration-300 ease-in-out
            hover:shadow hover:outline hover:outline-tttDefault hover:outline-[1px] hover:bg-tttDefault hover:bg-opacity-5
            disabled:opacity-50 disabled:cursor-not-allowed
          "
                >
                  Apply
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-1 w-full self-end text-sm text-gray-500">
              <p>First {TRIAL_PERIOD_DAYS} days</p>
              <p className="text-right text-green-600 font-semibold">FREE</p>
              <p>After trial</p>
              <p
                className={`text-right ${
                  Number.isFinite(amounts?.amount)
                    ? 'text-gray-800'
                    : 'text-gray-300'
                }`}
              >
                {amounts?.amount === undefined || amounts?.amount === 0
                  ? '$XXX.xx'
                  : `$${(amounts.amount / 100).toFixed(2)} / ${
                      selectedBillingPeriod === '3month'
                        ? '3 months'
                        : selectedBillingPeriod === 'year'
                        ? 'year'
                        : 'month'
                    }`}
              </p>
              {amounts?.discount !== undefined && (
                <>
                  <p>Discount</p>
                  <div
                    data-testid={'discount-amount'}
                    className={`flex text-right justify-end items-center ${
                      Number.isFinite(amounts?.amount)
                        ? 'text-gray-800'
                        : 'text-gray-300'
                    }`}
                  >
                    {applyCouponResponse.isLoading ? (
                      <Skeleton className="w-8 h-[90%] rounded-full bg-tttDefault bg-opacity-50" />
                    ) : (
                      ` -$${(amounts.discount / 100).toFixed(2)}`
                    )}
                  </div>
                </>
              )}
              <p>Tax</p>
              <div
                data-testid={`tax-amount`}
                className={`flex text-right text-sm justify-end items-center ${
                  Number.isFinite(amounts?.tax)
                    ? 'text-gray-800'
                    : 'text-gray-300'
                }`}
              >
                {isFetching ? (
                  <Skeleton className="w-8 h-[90%] rounded-full bg-tttDefault bg-opacity-50" />
                ) : amounts?.tax === undefined ? (
                  '$XX.xx'
                ) : (
                  `$${(amounts.tax / 100).toFixed(2)}`
                )}
              </div>
              <p className="text-base md:text-lg font-medium text-gray-800">
                Total
              </p>
              <div
                className={`flex font-medium text-right justify-end items-center ${
                  Number.isFinite(amounts?.total)
                    ? 'text-base md:text-lg text-gray-800'
                    : 'text-gray-300'
                }`}
              >
                {isFetching ? (
                  <Skeleton className="w-8 h-[90%] rounded-full bg-tttDefault bg-opacity-50" />
                ) : amounts?.total === undefined ? (
                  '$XXX.xx'
                ) : (
                  `$${(amounts.total / 100).toFixed(2)} / ${
                    selectedBillingPeriod === '3month'
                      ? '3 months'
                      : selectedBillingPeriod === 'year'
                      ? 'year'
                      : 'month'
                  }`
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default OrderDetails;
